import { Alert, AlertColor, Snackbar, SnackbarCloseReason } from '@mui/material';
import React, { createContext, useCallback, useContext, useState } from 'react';
import { logger } from '../utils/logger';

// Types for notifications
export interface NotificationMessage {
  id: string;
  message: string;
  severity: AlertColor;
  duration?: number;
}

interface NotificationContextType {
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
  showWarning: (message: string, duration?: number) => void;
  showInfo: (message: string, duration?: number) => void;
  showApiError: (error: string | any, fallbackMessage?: string) => void;
  dismissNotification: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationMessage[]>([]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const addNotification = useCallback(
    (message: string, severity: AlertColor, duration: number = 6000) => {
      const id = generateId();
      const notification: NotificationMessage = {
        id,
        message,
        severity,
        duration,
      };

      setNotifications(prev => [...prev, notification]);

      // Auto-dismiss after duration
      if (duration > 0) {
        setTimeout(() => {
          dismissNotification(id);
        }, duration);
      }

      return id;
    },
    []
  );

  const dismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const showSuccess = useCallback((message: string, _duration?: number) => {
    // Success messages are only logged, not shown in UI
    // Duration parameter kept for API compatibility but not used
    logger.info(message);
  }, []);

  const showError = useCallback(
    (message: string, duration?: number) => {
      addNotification(message, 'error', duration);
    },
    [addNotification]
  );

  const showWarning = useCallback(
    (message: string, duration?: number) => {
      addNotification(message, 'warning', duration);
    },
    [addNotification]
  );

  const showInfo = useCallback(
    (message: string, duration?: number) => {
      addNotification(message, 'info', duration);
    },
    [addNotification]
  );

  const showApiError = useCallback(
    (error: string | any, fallbackMessage: string = 'An error occurred') => {
      // Extract meaningful error message from API response
      let errorMessage = fallbackMessage;
      let detailedMessage = '';

      // Handle object (ApiResponse) input
      if (typeof error === 'object' && error !== null) {
        // Direct ApiResponse object
        if (error.message) {
          errorMessage = error.message;
          // If there's also an error field, show it as additional detail
          if (error.error && error.error !== error.message) {
            detailedMessage = ` (${error.error})`;
          }
        } else if (error.error) {
          errorMessage = error.error;
        } else if (error.details && Array.isArray(error.details) && error.details.length > 0) {
          // Handle validation errors
          errorMessage = error.details
            .map((detail: any) => `${detail.field}: ${detail.message}`)
            .join(', ');
        }
      } else if (typeof error === 'string') {
        try {
          // Try to parse if it's a JSON string
          const parsed = JSON.parse(error);

          // Priority: message > error > fallback
          if (parsed.message) {
            errorMessage = parsed.message;
            // If there's also an error field, show it as additional detail
            if (parsed.error && parsed.error !== parsed.message) {
              detailedMessage = ` (${parsed.error})`;
            }
          } else if (parsed.error) {
            errorMessage = parsed.error;
          } else if (parsed.details && Array.isArray(parsed.details) && parsed.details.length > 0) {
            // Handle validation errors
            errorMessage = parsed.details
              .map((detail: any) => `${detail.field}: ${detail.message}`)
              .join(', ');
          }
        } catch {
          // If not JSON, use the error string directly if it's meaningful
          if (error && error.length > 0 && error !== 'undefined' && error !== 'null') {
            errorMessage = error;
          }
        }
      }

      // Combine main message with details if available
      const fullMessage = errorMessage + detailedMessage;
      addNotification(fullMessage, 'error', 8000); // Longer duration for errors
    },
    [addNotification]
  );

  const handleSnackbarClose = (
    id: string,
    _event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    dismissNotification(id);
  };

  const contextValue: NotificationContextType = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showApiError,
    dismissNotification,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}

      {/* Render all notifications */}
      {notifications.map((notification, index) => (
        <Snackbar
          key={notification.id}
          open={true}
          autoHideDuration={notification.duration}
          onClose={(event, reason) => handleSnackbarClose(notification.id, event, reason)}
          sx={{
            // Stack multiple snackbars
            marginBottom: `${index * 70}px`,
          }}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <Alert
            onClose={() => dismissNotification(notification.id)}
            severity={notification.severity}
            variant="filled"
            sx={{
              width: '100%',
              fontSize: '0.875rem',
              '& .MuiAlert-message': {
                padding: '8px 0',
              },
            }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      ))}
    </NotificationContext.Provider>
  );
};

// Hook to use notifications
export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
